import { Component, ReactNode, Suspense, lazy, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Layout } from "@/components/Layout";
import { TierGate } from "@/components/TierGate";
import { PageErrorBoundary } from "@/components/PageErrorBoundary";
import { isClaraAppHost } from "@/lib/claraBase";
import { isConsultantAppHost } from "@/lib/consultantAppBase";
import { isRbHomeNext } from "@/lib/rbHomeVersion";
import { isFooteHost } from "@/lib/footeHost";
import { WALTER_HOME, isWalterHost } from "@/lib/walterHost";
import { isLobbyHost, isRaisedBeefHomeHost } from "@/lib/siteHost";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import PartnerUsageTracker from "@/components/PartnerUsageTracker";
import RequireAccount from "./pages/rbnext/account/RequireAccount";
import { isBarnApp, isBarnWebHome } from "@/barnApps/registry";
import BarnAppSplash from "@/barnApps/BarnAppSplash";
import { DashboardWidgetsProvider } from "./hooks/useDashboardWidgets";
import { UserProfileProvider } from "./hooks/useUserProfile";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { NavModeProvider } from "@/contexts/NavModeContext";

// ---------------------------------------------------------------------------
// Route-level code splitting — each page is loaded on demand, dramatically
// reducing the initial bundle size.  The ErrorBoundary already handles stale
// chunk-load errors with an automatic page reload.
// ---------------------------------------------------------------------------
const Auth = lazy(() => import("./pages/Auth"));
const FooteSignIn = lazy(() => import("./pages/foote/FooteSignIn"));
// Walter — product 8, Agricultural Balance Sheets (walter.raisedbeef.com).
const WalterSignIn = lazy(() => import("./pages/walter/WalterSignIn"));
const WalterUsBalanceSheet = lazy(() => import("./pages/walter/WalterUsBalanceSheet"));
const WalterWorldTable = lazy(() => import("./pages/walter/WalterWorldTable"));
const WalterThisMonth = lazy(() => import("./pages/walter/WalterThisMonth"));
const WalterFas = lazy(() => import("./pages/walter/WalterFasPage"));
const WalterLivestock = lazy(() => import("./pages/walter/WalterLivestockPage"));
const WalterAsk = lazy(() => import("./pages/walter/WalterAskPage"));
const WalterCornDesk = lazy(() => import("./pages/walter/WalterCornDeskPage"));
const WalterStories = lazy(() => import("./pages/walter/WalterStoriesPage"));
const WalterStoryEditor = lazy(() => import("./pages/walter/WalterStoryEditorPage"));
const WalterSharedStory = lazy(() => import("./pages/walter/WalterSharedStoryPage"));
const WalterReadingRoom = lazy(() => import("./pages/walter/WalterReadingRoomPage"));
const WalterReaderStory = lazy(() => import("./pages/walter/WalterReaderStoryPage"));
const FooteHome = lazy(() => import("./pages/foote/FooteHome"));
const FooteBarn = lazy(() => import("./pages/foote/FooteBarn"));
const FooteState = lazy(() => import("./pages/foote/FooteState"));
const FooteBoxedBeef = lazy(() => import("./pages/foote/FooteBoxedBeef"));
const FooteFiveArea = lazy(() => import("./pages/foote/FooteFiveArea"));
const FooteFatCattle = lazy(() => import("./pages/foote/FooteFatCattle"));
const FooteFatBarn = lazy(() => import("./pages/foote/FooteFatBarn"));
const FootePackerMargin = lazy(() => import("./pages/foote/FootePackerMargin"));
const FooteWeightedAverages = lazy(() => import("./pages/foote/FooteWeightedAverages"));
const FooteWeightMap = lazy(() => import("./pages/foote/FooteWeightMap"));
const FooteCarcassWeights = lazy(() => import("./pages/foote/FooteCarcassWeights"));
const FooteCattleOnFeed = lazy(() => import("./pages/foote/FooteCattleOnFeed"));
const FooteDailySlaughter = lazy(() => import("./pages/foote/FooteDailySlaughter"));
const FooteBarnPrices = lazy(() => import("./pages/foote/FooteBarnPrices"));
const FooteWeightSlide = lazy(() => import("./pages/foote/FooteWeightSlide"));
const FooteLeaderboard = lazy(() => import("./pages/foote/FooteLeaderboard"));
const FooteAllBarns = lazy(() =>
  import("./pages/foote/FooteDirectory").then((m) => ({ default: m.FooteAllBarns })),
);
const FooteAllStates = lazy(() =>
  import("./pages/foote/FooteDirectory").then((m) => ({ default: m.FooteAllStates })),
);
const FooteAddBarns = lazy(() => import("./pages/foote/FooteAddBarns"));
const FooteAddStates = lazy(() => import("./pages/foote/FooteAddStates"));
const FooteAddTools = lazy(() => import("./pages/foote/FooteAddTools"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OnboardingOperatorType = lazy(() => import("./pages/OnboardingOperatorType"));
const ClaraLiveHome = lazy(() => import("./pages/ClaraLiveHome"));

// Clara Live mobile shell — Phase 1. Lives at /clara/* alongside the
// existing desktop ClaraLiveHome at /. Additive only; desktop app is
// unchanged. Positioning flip (make /clara the default) is a Phase 8
// decision, not a Phase 1 concern.
const ConsultantAppShell = lazy(() => import("./pages/consultant-app/ConsultantAppShell"));
const ConsultantAppAuth = lazy(() => import("./pages/consultant-app/AppAuth"));
const ClaraMobileShell = lazy(() => import("./components/clara/mobile/ClaraShell").then(m => ({ default: m.ClaraShell })));
const ClaraMobileHome = lazy(() => import("./pages/clara-app/Home"));
const ClaraMobileSales = lazy(() => import("./pages/clara-app/Sales"));
const ClaraMobileContact = lazy(() => import("./pages/clara-app/Contact"));
const ClaraMobileAccount = lazy(() => import("./pages/clara-app/Account"));
const ClaraMobileSignIn = lazy(() => import("./pages/clara-app/SignIn"));
const ClaraMobileAlerts = lazy(() => import("./pages/clara-app/Alerts"));
const ClaraMobileTopic = lazy(() => import("./pages/clara-app/Topic"));
const ClaraMobileBarn = lazy(() => import("./pages/clara-app/Barn"));
const ClaraMobileBarnTool = lazy(() => import("./pages/clara-app/BarnTool"));
// ⚠️ RETIRED 2026-09-18 (owner, PRODUCTS.md): the `/app` shell, My Cattle, and
// the duplicate My Barn that lived inside product 7. None was a product. Between
// them they carried two of the six login doors and a second copy of the barn
// dashboard whose access checks were weaker than the real one's — it took a
// membership where the real door takes a role.
//
// The real My Barn (`src/pages/MyBarn.tsx` + `src/barnApps/mybarn/*`) is
// untouched and remains the only one.
import NativeBoot from "./components/NativeBoot";
import PolicyAckBootstrap from "./components/PolicyAckBootstrap";
import MyBarnLogin from "./pages/MyBarnLogin";
const MarketDetailPage = lazy(() => import("./pages/MarketDetailPage"));
const FreeReportPage = lazy(() => import("./pages/reports/FreeReportPage"));
const BarnDetailPage = lazy(() => import("./pages/BarnDetailPage"));
const BarnSaleReportPreview = lazy(() => import("./pages/BarnSaleReportPreview"));
const MyBarn = lazy(() => import("./pages/MyBarn"));
const BarnSaleArchive = lazy(() => import("./pages/BarnSaleArchive"));
const EmbedBarn = lazy(() => import("./pages/EmbedBarn"));
const CohortDetailPage = lazy(() => import("./pages/CohortDetailPage"));
const PremiumReportPage = lazy(() => import("./pages/reports/PremiumReportPage"));

const RegionalMarkets = lazy(() => import("./pages/RegionalMarkets"));
const SupplyDemand = lazy(() => import("./pages/SupplyDemand"));
const FreightIntelligence = lazy(() => import("./pages/FreightIntelligence"));
const ProfitabilityMargins = lazy(() => import("./pages/ProfitabilityMargins"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const ReportBuilder = lazy(() => import("./pages/ReportBuilder"));
const DataIntegrations = lazy(() => import("./pages/DataIntegrations"));
const DataArchitecture = lazy(() => import("./pages/DataArchitecture"));
const MarketDataSchema = lazy(() => import("./pages/MarketDataSchema"));

const NewsDailyBriefs = lazy(() => import("./pages/NewsDailyBriefs"));
const StockerBriefs = lazy(() => import("./pages/StockerBriefs"));
const SaleDayBriefs = lazy(() => import("./pages/SaleDayBriefs"));
const FeedyardBriefs = lazy(() => import("./pages/FeedyardBriefs"));
const BriefsHub = lazy(() => import("./pages/BriefsHub"));
const WeeklyDigests = lazy(() => import("./pages/WeeklyDigests"));
const DVAuctionReports = lazy(() => import("./pages/DVAuctionReports"));
const MarketReport = lazy(() => import("./pages/MarketReport"));
const CalfTracker = lazy(() => import("./pages/CalfTracker"));
const EmailDashboard = lazy(() => import("./pages/EmailDashboard"));
const AdminSpotlights = lazy(() => import("./pages/AdminSpotlights"));
const AdminCmcLive = lazy(() => import("./pages/AdminCmcLive"));
const AdminDataHealth = lazy(() => import("./pages/AdminDataHealth"));
const AdminClaraSponsors = lazy(() => import("./pages/AdminClaraSponsors"));
const AdminBarnSponsors = lazy(() => import("./pages/AdminBarnSponsors"));
const AdminBoardAds = lazy(() => import("./pages/AdminBoardAds"));
const AdminBarnSubscriptions = lazy(() => import("./pages/AdminBarnSubscriptions"));
const AdminPartnerUsage = lazy(() => import("./pages/AdminPartnerUsage"));
const AdminCommentaryFacts = lazy(() => import("./pages/AdminCommentaryFacts"));
const AdminSales = lazy(() => import("./pages/AdminSales"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminBarns = lazy(() => import("./pages/AdminBarns"));
const AdminBarnApps = lazy(() => import("./pages/AdminBarnApps"));
const AdminConsultantReview = lazy(() => import("./pages/AdminConsultantReview"));
const AdminNetworkMembers = lazy(() => import("./pages/AdminNetworkMembers"));
const NetworkDesk = lazy(() => import("./pages/NetworkDesk"));
const AdminNetworkCompliance = lazy(() => import("./pages/AdminNetworkCompliance"));
const AdminCofReportPreview = lazy(() => import("./pages/AdminCofReportPreview"));
const AdminCofReports = lazy(() => import("./pages/AdminCofReports"));
const AdminCofReportDraft = lazy(() => import("./pages/AdminCofReportDraft"));
const CofReportPublic = lazy(() => import("./pages/CofReportPublic"));
// MarketShiftTools removed — Seasonal Deviation + Placement Optimizer were
// redundant with PMI matrix; Volume Anomaly will be reworked into the
// Supply & Demand section as a separate widget.
const CattleOnFeed = lazy(() => import("./pages/CattleOnFeed"));
const FedCattleAuctionBarometer = lazy(() => import("./pages/FedCattleAuctionBarometer"));
const FatCattleSaleResults = lazy(() => import("./pages/FatCattleSaleResults"));
const BarnVsPackerPaid = lazy(() => import("./pages/BarnVsPackerPaid"));
const CattleInventory = lazy(() => import("./pages/CattleInventory"));
const MarketPulse = lazy(() => import("./pages/MarketPulse"));
const PastureStress = lazy(() => import("./pages/PastureStress"));
const PerHeadSupply = lazy(() => import("./pages/PerHeadSupply"));
const ColdStorage = lazy(() => import("./pages/ColdStorage"));
const CotPositioning = lazy(() => import("./pages/CotPositioning"));
const MarketReportCalendar = lazy(() => import("./pages/MarketReportCalendar"));
const FeedingExtension = lazy(() => import("./pages/FeedingExtension"));
const ValueAddedPremium = lazy(() => import("./pages/ValueAddedPremium"));
const BarnPriceLadder = lazy(() => import("./pages/BarnPriceLadder"));
const ValueOfGain = lazy(() => import("./pages/ValueOfGain"));
const VideoForwardMarket = lazy(() => import("./pages/VideoForwardMarket"));
const PackerPosture = lazy(() => import("./pages/PackerPosture"));
const PackerLeverage = lazy(() => import("./pages/PackerLeverage"));
const SeasonalCashSetup = lazy(() => import("./pages/SeasonalCashSetup"));
const RegionalPulse = lazy(() => import("./pages/RegionalPulse"));
const NationalSteerTracker = lazy(() => import("./pages/NationalSteerTracker"));
const StateFeederDirect = lazy(() => import("./pages/StateFeederDirect"));
const SteerTrackerDaily = lazy(() => import("./pages/SteerTrackerDaily"));
const ClaraLiveTracker = lazy(() => import("./pages/ClaraLiveTracker"));
const ConsultantPage = lazy(() => import("./pages/ConsultantPage"));
const ClaraLiveLanding = lazy(() => import("./pages/ClaraLiveLanding"));
// raisedbeef.com → DVAuction Live Sales. This was the "front gate" homepage
// until 2026-08-09, when `/` became the product gate below.
const DvAuctionLivePage = lazy(() => import("./pages/raisedbeef/DvAuctionLivePage"));
// raisedbeef.com `/` — the product gate. Normally NOT reached through this
// router: vercel.json rewrites `/` on that host to the prerendered
// rb-home.html so Google gets real markup. Mounted here anyway so the page
// still renders if the rewrite is missing, and so `?site=home` previews it
// locally. Same component either way.
const RbGateHome = lazy(() => import("./pages/raisedbeef/RbGateHome"));
const RbNextHome = lazy(() => import("./pages/rbnext/RbNextHome"));
// The lobby board, behind Premium (owner, 2026-10-12). src/lobby/LobbyPremium.tsx.
const LobbyTickerPage = lazy(() => import("./lobby/LobbyPremium"));
const FatCattleHub = lazy(() => import("./pages/rbnext/fat-cattle/FatCattleHub"));
const FatCattleBarnPage = lazy(() => import("./pages/rbnext/fat-cattle/FatCattleBarnPage"));

// The RaisedBeef phone app (owner, 2026-09-16). Distinct from /app (the
// Terminal in a phone) and /clara (the consultant) — three shells, three
// products, none of them replacing another.
const RbAppShell = lazy(() => import("./rbapp/RbAppShell"));
const RbAppHome = lazy(() => import("./rbapp/HomeScreen"));
const RbAppIndex = lazy(() => import("./rbapp/IndexScreen"));
const RbAppSitePage = lazy(() => import("./rbapp/SitePage"));
const RbAppWeightedAverages = lazy(() => import("./rbapp/WeightedAveragesScreen"));
const RbAppClaraLight = lazy(() => import("./rbapp/ClaraLightScreen"));
const RbAppBarnLanding = lazy(() => import("./rbapp/BarnLandingScreen"));
const RbAppAlertInbox = lazy(() => import("./rbapp/AlertInboxScreen"));
const RbAppAlertReach = lazy(() => import("./rbapp/AlertsScreen").then((m) => ({ default: m.AlertReachScreen })));
const RbAppQuotesBoard = lazy(() => import("./rbapp/QuotesBoardScreen"));
const RbAppLeaderboard = lazy(() => import("./rbapp/LeaderboardScreen"));
const RbAppFeederFlash = lazy(() => import("./rbapp/FeederFlashScreen"));
const RbAppToppers = lazy(() => import("./rbapp/ToppersScreen"));
const RbAppClaraPro = lazy(() => import("./rbapp/ClaraProScreen"));
const RbAppWeightMaps = lazy(() => import("./rbapp/WeightMapsScreen"));
const RbAppBoxedBeef = lazy(() => import("./rbapp/BoxedBeefScreen"));
const RbAppFedCattle = lazy(() => import("./rbapp/FedCattleScreen"));
const RbAppIndexDay = lazy(() => import("./rbapp/IndexDayScreen"));
const RbAppMethodology = lazy(() => import("./rbapp/MethodologyScreen"));
const RbAppPackerMargin = lazy(() => import("./rbapp/PackerMarginScreen"));
const RbAppCattleOnFeed = lazy(() => import("./rbapp/CattleOnFeedScreen"));
const RbAppDiesel = lazy(() => import("./rbapp/DieselScreen"));
const RbAppBorder = lazy(() => import("./rbapp/BorderScreen"));
const RbAppReceipts = lazy(() => import("./rbapp/ReceiptsScreen"));
const RbAppIndexes = lazy(() => import("./rbapp/IndexesScreen"));
const RbAppSubscribe = lazy(() => import("./rbapp/SubscribeScreen"));
const RbAppSave = lazy(() => import("./rbapp/SaveAccountScreen"));
const RbAppAccount = lazy(() => import("./rbapp/AccountScreen"));
const RbAppSteerTracker = lazy(() => import("./rbapp/SteerTrackerScreen"));
const RbAppFatHub = lazy(() => import("./rbapp/FatCattleScreens").then((m) => ({ default: m.FatCattleHubRoute })));
const RbAppFatBarn = lazy(() => import("./rbapp/FatCattleScreens").then((m) => ({ default: m.FatCattleBarnRoute })));
const RbAppReports = lazy(() => import("./rbapp/SaleReportsScreens").then((m) => ({ default: m.SaleReportsHomeRoute })));
const RbAppReport = lazy(() => import("./rbapp/SaleReportsScreens").then((m) => ({ default: m.SaleReportRoute })));
const RbAppState = lazy(() => import("./rbapp/StateScreen"));
const RbAppPrices = lazy(() => import("./rbapp/PricesScreen"));
const RbAppBarns = lazy(() => import("./rbapp/BarnsScreen"));
const RbAppBarnBoard = lazy(() => import("./rbapp/BarnBoardScreen"));
const RbAppYou = lazy(() => import("./rbapp/YouScreen"));
const RbAppTools = lazy(() => import("./rbapp/ToolsScreen"));
const RbAppAlerts = lazy(() => import("./rbapp/AlertsScreen"));
const SteerTrackerPublic = lazy(() => import("./pages/rbnext/steer-tracker/SteerTrackerPage"));
// ⚠️ THE LEADERBOARD THE SITE SERVES IS THE V2 PAGE (owner, 2026-09-26). It was
// built and settled at /leaderboard-preview, then promoted; that preview route
// is gone. The page it replaced is still on disk and `LeaderboardPrev` below is
// wired to nothing — putting it back is a one-word change on the route.
// The National Leaderboard rebuilt to the owner's approved mock (2026-10-15,
// design/leaderboard/README.md). The v2 page stays on disk; putting it back
// is this one line.
const LeaderboardNext = lazy(() => import("./pages/rbnext/leaderboard-v3/LeaderboardV3Page"));
const RbManageLeaderboardPage = lazy(() => import("./pages/rbnext/manage/RbManageLeaderboardPage"));
const RbToppersPage = lazy(() => import("./pages/rbnext/toppers/ToppersPage"));
// Where the wall-display promotion's CTA lands. Holding page until the real
// inquiry form is built — see the file header.
const RbWallDisplayPage = lazy(() => import("./pages/rbnext/wall-display/WallDisplayPage"));
// Clara Light — boxed beef, written by arithmetic rather than a model.
const RbClaraLightPage = lazy(() => import("./pages/rbnext/clara-light/ClaraLightPage"));
const LeaderboardPrev = lazy(() => import("./pages/rbnext/leaderboard/LeaderboardPage"));
void LeaderboardPrev;
const RbAccountPage = lazy(() => import("./pages/rbnext/account/RbAccountPage"));
const RbManageToolsPage = lazy(() => import("./pages/rbnext/manage/RbManageToolsPage"));
const BarnTickerPublic = lazy(() => import("./pages/rbnext/ticker/BarnTickerPage"));
const BarnLeaderboardPublic = lazy(() => import("./pages/rbnext/barn-leaderboard/BarnLeaderboardRoute"));
const BarnLivePublic = lazy(() => import("./pages/rbnext/barn-live/BarnLivePage"));
const StateTickerPage = lazy(() => import("./pages/rbnext/state-ticker/StateTickerPage"));
const BarnsIndexPage = lazy(() => import("./pages/rbnext/directory/BarnsIndexPage"));
const StatesIndexPage = lazy(() => import("./pages/rbnext/directory/StatesIndexPage"));
// raisedbeef.com/solutions — the services page. Also prerendered (served via a
// vercel.json rewrite to solutions.html, which works here because no file
// matches "/solutions"). Mounted so the route still resolves if the rewrite
// is missing, and so it works on every other host.
const RbSolutionsPage = lazy(() => import("./pages/raisedbeef/RbSolutionsPage"));
const PlatformPage = lazy(() => import("./pages/raisedbeef/PlatformPage"));
const BarnAppShell = lazy(() => import("./barnApps/BarnAppShell"));
// Smart board — the in-barn 86" touchscreen (SMART_BOARD_PLAN.md). A shared
// wall-mounted device with no user and no tier, so it mounts OUTSIDE Layout.
const BoardShell = lazy(() => import("./board/BoardShell"));
const CmcLiveHome = lazy(() => import("./cmcLive/CmcLiveHome"));
const CmcLiveBarn = lazy(() => import("./cmcLive/CmcLiveBarn"));
const BarnWebHome = lazy(() => import("./barnApps/BarnWebHome"));
const LeaderboardPage = lazy(() => import("./pages/raisedbeef/LeaderboardPage"));
const SalesPage = lazy(() => import("./pages/raisedbeef/SalesPage"));
// The public Boxed Beef tool, in the RaisedBeef `.rbtx` UI. It replaced the
// simpler pages/raisedbeef/BoxedBeefPage on 2026-09-13 — that one showed
// today plus a 7/30/90 trend and nothing else.
// ⚠️ NOT a replacement for /tools/packer-posture, which stays in the
// terminal, choice-gated, with the cuts and the AI narrative.
const RbBoxedBeefPage = lazy(() => import("./pages/rbnext/boxed-beef/BoxedBeefPage"));
const RbPackerMarginPage = lazy(() => import("./pages/rbnext/packer-margin/PackerMarginPage"));
const RbBorderPage = lazy(() => import("./pages/rbnext/border/BorderPage"));
const RbFlashPage = lazy(() => import("./pages/rbnext/flash/FlashPage"));
const RbCattleOnFeedPage = lazy(() => import("./pages/raisedbeef/CattleOnFeedPage"));
// The public Fed Cattle tool, in the RaisedBeef `.rbtx` UI. It replaced
// pages/raisedbeef/FedCattlePage on 2026-09-13. That page led with the National
// Weekly Comprehensive price while the homepage rail card that linked to it led
// with 5-Area — two USDA reports $7.91/cwt apart, with nothing on the page
// saying so. This one leads with 5-Area, matching the card, and carries the
// Comprehensive as a labelled second figure.
const RbFedCattlePage = lazy(() => import("./pages/rbnext/fed-cattle/FedCattlePage"));
const RbSaleReportsPage = lazy(() => import("./pages/raisedbeef/SaleReportsPage"));
const RbReportsPage = lazy(() => import("./pages/rbnext/reports/ReportsPage"));
const RbWeightMapPage = lazy(() => import("./pages/rbnext/weight-map/WeightMapPage"));
const RbDieselPage = lazy(() => import("./pages/rbnext/diesel/DieselPage"));
const RbIndexPage = lazy(() => import("./pages/rbnext/index-page/IndexPage"));
const RbIndexReconciliationPage = lazy(() => import("./pages/rbnext/index-page/ReconciliationPage"));
const RbIndexSheetPage = lazy(() => import("./pages/rbnext/index-page/SheetPage"));
const RbDailyPathPage = lazy(() => import("./pages/rbnext/index-page/DailyPathPage"));
const RbIndexCorrectionsPage = lazy(() => import("./pages/rbnext/index-page/CorrectionsPage"));
const RbQuotesBoardPage = lazy(() => import("./pages/rbnext/tools/QuotesBoardPage"));
const RbWeeklyAveragesPage = lazy(() => import("./pages/rbnext/tools/WeeklyAveragesPage"));
const RbDailyMethodologyPage = lazy(() => import("./pages/rbnext/index-page/DailyMethodologyPage"));
const RbIndexMethodologyPage = lazy(() => import("./pages/rbnext/index-page/MethodologyPage"));
// The plan, sold on purpose (owner, 2026-10-25). Public: never gated.
const RbMembershipPage = lazy(() => import("./pages/rbnext/account/MembershipPage"));
const RbJoinPage = lazy(() => import("./pages/rbnext/account/JoinPage"));
const RbIndexAuditPage = lazy(() => import("./pages/rbnext/index-audit/IndexAuditPage"));
const RbSubscribersPage = lazy(() => import("./pages/rbnext/subscribers/SubscribersPage"));
const RbReceiptsPage = lazy(() => import("./pages/rbnext/receipts/ReceiptsPage"));
const RbDailyReportPage = lazy(() => import("./pages/rbnext/daily-report/ReportPage"));
const RbManageDailyReportPage = lazy(() => import("./pages/rbnext/daily-report/ManageDailyReportPage"));
const RbManageCmcLivePage = lazy(() => import("./pages/rbnext/manage/RbManageCmcLivePage"));
const RbSaleSheetPage = lazy(() => import("./pages/rbnext/sale-sheet/SaleSheetPage"));
const RbSaleSheetLanding = lazy(() => import("./pages/rbnext/sale-sheet/SaleSheetLanding"));
const Upgrade = lazy(() => import("./pages/Upgrade"));
const Pricing = lazy(() => import("./pages/Pricing"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const CheckoutCancel = lazy(() => import("./pages/CheckoutCancel"));
const Unsubscribed = lazy(() => import("./pages/Unsubscribed"));

// Section hub pages — Phase 3 of the navigation redesign.
// Each hub is the landing route for a sidebar section and renders
// decision-grouped tool tiles via <SectionHub>.
const FeedersHub = lazy(() => import("./pages/hubs/FeedersHub"));
const FedCattleHub = lazy(() => import("./pages/hubs/FedCattleHub"));
const MyOperationHub = lazy(() => import("./pages/hubs/MyOperationHub"));
const InsightsHub = lazy(() => import("./pages/hubs/InsightsHub"));
// Phase 2 hubs (Session 53 sidebar reorg).
const BoxedBeefHub = lazy(() => import("./pages/hubs/BoxedBeefHub"));
const PrimalSeasonality = lazy(() => import("./pages/PrimalSeasonality"));
const ChoiceDailyCutout = lazy(() => import("./pages/ChoiceDailyCutout"));
const WasdeFedCattleForecast = lazy(() => import("./pages/WasdeFedCattleForecast"));
const WasdeForecastPath = lazy(() => import("./pages/WasdeForecastPath"));
const WasdeTrackRecord = lazy(() => import("./pages/WasdeTrackRecord"));
const WasdeCornOutlook = lazy(() => import("./pages/WasdeCornOutlook"));
const WasdeThisMonth = lazy(() => import("./pages/WasdeThisMonth"));
const WasdeBeefSupply = lazy(() => import("./pages/WasdeBeefSupply"));
const HolidayCutTracker = lazy(() => import("./pages/HolidayCutTracker"));
const SteakBurgerGauge = lazy(() => import("./pages/SteakBurgerGauge"));
const CullBullsHub = lazy(() => import("./pages/hubs/CullBullsHub"));
const CowCalfHub = lazy(() => import("./pages/hubs/CowCalfHub"));
const CowCalfEconomics = lazy(() => import("./pages/CowCalfEconomics"));
const SlaughterHub = lazy(() => import("./pages/hubs/SlaughterHub"));
const CattleCycleHub = lazy(() => import("./pages/hubs/CattleCycleHub"));
const RecentTradeRecap = lazy(() => import("./pages/RecentTradeRecap"));
const DayOfWeekPattern = lazy(() => import("./pages/DayOfWeekPattern"));
const ChoiceSelectTracker = lazy(() => import("./pages/ChoiceSelectTracker"));
const FeedlotLeverageIndex = lazy(() => import("./pages/FeedlotLeverageIndex"));
const CaptiveSupplyTracker = lazy(() => import("./pages/CaptiveSupplyTracker"));
const CashVsGridSpread = lazy(() => import("./pages/CashVsGridSpread"));
const PriceDistribution = lazy(() => import("./pages/PriceDistribution"));
const CashSeasonalityCalendar = lazy(() => import("./pages/CashSeasonalityCalendar"));
// PlantPace retired Session 60 — Seasonal Kill Range chart merged into MarketPulse;
// daily + weekly charts were redundant with Kill Pace Monitor section.
// QuickAccess page removed — replaced by global search bar in TopNav.
// QuickView detail pages (barn/state/region) remain accessible via search.
const QuickViewBarn = lazy(() => import("./pages/QuickViewBarn"));
const QuickViewState = lazy(() => import("./pages/QuickViewState"));
const QuickViewRegion = lazy(() => import("./pages/QuickViewRegion"));
const DroughtIntelligence = lazy(() => import("./pages/DroughtIntelligence"));
const AssumptionCheck = lazy(() => import("./pages/AssumptionCheck"));
const DroughtPressure = lazy(() => import("./pages/DroughtPressure"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Terms = lazy(() => import("./pages/Terms"));
const Features = lazy(() => import("./pages/Features"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
// The SALE BARN APP's own policy + terms. Separate from /privacy and /terms,
// which are the platform's — a barn app has no messaging, no data sale and no
// subscription, so it must not point a store listing at a policy describing
// them. Same markdown the app bundles and shows in-app (BarnAppLegal.tsx).
const BarnPrivacy = lazy(() => import("./pages/BarnPrivacy"));
const BarnTerms = lazy(() => import("./pages/BarnTerms"));
const BarnSupport = lazy(() => import("./pages/BarnSupport"));
const BarnDeletion = lazy(() => import("./pages/BarnDeletion"));
const CommunityGuidelines = lazy(() => import("./pages/CommunityGuidelines"));
const DoNotSell = lazy(() => import("./pages/DoNotSell"));
const Disclaimer = lazy(() => import("./pages/Disclaimer"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const AcceptableUse = lazy(() => import("./pages/AcceptableUse"));
const Support = lazy(() => import("./pages/Support"));
const RbLegalPage = lazy(() => import("./pages/rbnext/legal/RbLegalPage"));
const AccountDeletion = lazy(() => import("./pages/AccountDeletion"));

function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message = err instanceof Error ? err.message : String(err);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    // Safari's wording when the old chunk's address now returns the site's
    // index.html (the catch-all rewrite) instead of JavaScript — what an
    // iPhone with the app open saw after every deploy (owner's screenshot,
    // 2026-10-09: "'text/html' is not a valid JavaScript MIME type.").
    message.includes("is not a valid JavaScript MIME type") ||
    /Loading chunk \d+ failed/.test(message) ||
    /Unable to preload CSS/.test(message)
  );
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; message: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, message: null };
  }
  static getDerivedStateFromError(error: Error) {
    // Stale-bundle errors auto-recover via main.tsx's preloadError handler,
    // but if that misses we fall through to here. A chunk-load error means
    // a deploy happened — force a reload instead of showing the user a
    // "Something went wrong" screen they don't know how to recover from.
    if (isChunkLoadError(error)) {
      // Time-based guard (matches main.tsx): allow another auto-reload if
      // the last one was >20s ago — rapid-deploy days trigger multiple
      // legitimate stale-chunk recoveries per session.
      const flag = "raisedbeef:chunk-reload-eb";
      try {
        const last = Number(sessionStorage.getItem(flag) ?? 0);
        if (Date.now() - last >= 20_000) {
          sessionStorage.setItem(flag, String(Date.now()));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
      return { hasError: false };
    }
    // ⚠️ THE MESSAGE IS KEPT AND SHOWN. This screen used to say only
    // "Something went wrong. Please refresh." — which tells the user nothing
    // they can act on and tells whoever is debugging even less. A real failure
    // cost a support round-trip and two wrong guesses before anyone could see
    // what actually threw, because the only copy of the message was a console
    // line nobody on a phone can reach.
    return { hasError: true, message: error?.message ?? null };
  }
  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("Dashboard render error", error, info.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center text-center p-8">
          <div>
            <h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">An unexpected error occurred. Please refresh the page.</p>
            {this.state.message ? (
              <p className="mx-auto mb-4 max-w-md break-words rounded bg-muted/50 px-3 py-2 text-left font-mono text-[11px] leading-relaxed text-muted-foreground">
                {this.state.message}
              </p>
            ) : null}
            <button className="underline text-sm" onClick={() => this.setState({ hasError: false, message: null })}>Try again</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * The free-account gate on raisedbeef.com's deeper tools (owner, 2026-09-15).
 *
 * Registering opens these for 30 days, free, no card. The free layer — the
 * homepage board, every barn ticker, every state ticker, the directories,
 * Boxed Beef and Fed Cattle — is NOT wrapped and never asks for anything.
 *
 * ⚠️ raisedbeef.com ONLY. markets.raisedbeef.ai already requires a paid
 * account and gates through TierGate; wrapping these routes there would put a
 * second, different door in front of a page a subscriber has already paid for.
 *
 * The list of wrapped routes must match `requiresAccount()` in
 * src/lib/siteAccess.ts — siteAccessRoutes.test.ts reads both and fails if they
 * drift, because a page in one list and not the other is either an open door
 * or a wall nobody meant to build.
 */
/**
 * /clara-light → /clara, carrying the query string.
 *
 * ⚠️ THE SEARCH IS THE POINT. `<Navigate to="/clara" replace />` discards it,
 * and every shared /clara-light?topic=cold link would open the front door
 * instead of cold storage.
 */
function ClaraLightRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/clara${search}`} replace />;
}

const gated = (element: React.ReactElement): React.ReactElement =>
  isRaisedBeefHomeHost() ? <RequireAccount>{element}</RequireAccount> : element;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,        // 5 min — market data doesn't change second-by-second
      gcTime: 10 * 60 * 1000,           // 10 min garbage collection
      refetchOnWindowFocus: false,       // Prevent refetch storms on tab switch
      retry: 1,                          // Single retry is sufficient for Supabase
      refetchIntervalInBackground: false, // Never poll when tab is hidden
    },
  },
});

const RouteFallback = () => (
  <div className="flex min-h-screen items-center justify-center">
    <div className="animate-pulse text-muted-foreground text-sm">Loading…</div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <UserProfileProvider>
    <ThemeProvider>
    <DashboardWidgetsProvider>
    <NavModeProvider>
    <TooltipProvider>

      {isBarnApp() ? (
      /* White-label single-barn build (VITE_BARN_APP=<slug>) — the whole app
         is that barn's branded shell; the platform router is bypassed. The boot
         splash (barn logo on the brand color) covers the shell chunk load.
         Also reached at a barn's PUBLIC web address (raisedbeef.com/mitchell),
         where isBarnWebHome() adds the desktop phone frame + store buttons
         around this same shell. The shipped native app must never take that
         branch — it is already inside a phone. */
      isBarnWebHome()
        ? <Suspense fallback={<BarnAppSplash />}><BarnWebHome /></Suspense>
        : <Suspense fallback={<BarnAppSplash />}><BarnAppShell /></Suspense>
      ) : (
      <>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        {/* Google Analytics page views — websites only; see src/lib/analytics.ts. */}
        <AnalyticsTracker />
        {/* How long a partner actually spends on their platform, and on which
            tool. Partner hosts only — renders null everywhere else. See
            src/components/PartnerUsageTracker.tsx for why the scope is narrow. */}
        <PartnerUsageTracker />
        <NativeBoot />
        <PolicyAckBootstrap />
        {/* ⚠️ BarnOnlyLock WAS HERE AND IS GONE (owner, 2026-09-13).
            It ran on every navigation, and when the signed-in session belonged
            to a barn it replaced the URL with /my-barn. That is the coupling
            the owner asked to remove: "I don't want my barn attached to this
            website login."

            It was never security — RLS, the SECURITY DEFINER RPCs and
            has_role() are what keep non-members out of a barn's data, and all
            of that is untouched. This was a UX redirect, and what it actually
            produced was an hour of confusion: a Stockmen's session left open in
            a browser made every tool on the platform bounce to a barn dashboard
            that had nothing to do with the person reading it. */}
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Public routes — accessible without authentication */}
          <Route path="/auth" element={<Auth />} />

          {/* Smart board — one barn's lobby touchscreen, themed from that
              barn's registry entry. Deliberately outside Layout: the board is
              a shared device with nobody logged in, and the barn product has
              no tier, so it must never reach TierGate. */}
          <Route path="/board/:barnSlug" element={<BoardShell />} />

          {/* CMC Live — the all-day market news timeline. Deliberately outside
              Layout and open to anyone: the whole product is read free, share
              free, then get teased into Cattle Market Central (owner,
              2026-10-23). Reads public.cmc_live_feed / cmc_live_scoreboard,
              which are granted to anon for exactly this reason. Becomes the
              CMC Live phone app's home screen (phase 3). */}
          <Route path="/cmc-live" element={<CmcLiveHome />} />
          {/* A barn's own CMC Live page: 30 days of every story that names it,
              badges included, each with a share link. Where raisedbeef.com/live/<id>
              lands a person. Public like the timeline. */}
          <Route path="/cmc-live/b/:slug" element={<CmcLiveBarn />} />

          {/* Clara Live mobile app. On the dedicated app host
              (app.raisedbeef.com) it owns the domain and mounts at "/";
              on every other host it lives under "/clara". claraBase.ts
              keeps the app's internal links in lockstep with this. */}
          {/* The standalone consultant app. Owns the root on its own domain
              and mounts at /ask everywhere else — a shell reachable only once
              DNS exists is a shell nobody can look at. See consultantAppBase. */}
          <Route
            path={isConsultantAppHost() ? "/" : "/ask"}
            element={<ConsultantAppShell />}
          />
          {/* The app's own sign-in. Standalone and outside every auth wrapper,
              so a logged-out visitor lands here rather than on the terminal's
              /auth — which would sign them into a product they did not open. */}
          <Route
            path={isConsultantAppHost() ? "/signin" : "/ask/signin"}
            element={<ConsultantAppAuth />}
          />

          {/* ⚠️ THE CLARA LIVE MOBILE SHELL NO LONGER CLAIMS /clara ON
              raisedbeef.com. Owner, 2026-10-03: "The Clara app is no more what
              we are creating today will take its place we are retiring the old
              Clara app." The new Clara door takes that path on the home host.

              ⚠️ NOTHING IS DELETED HERE. The app still mounts at "/" on its own
              host (app.raisedbeef.com) and at /clara on every other host, so
              the shipped native build and markets.raisedbeef.ai are untouched.
              Retiring it properly means deciding what people with the app
              installed see and what app.raisedbeef.com serves afterwards —
              decisions, not deletions, and not this change. */}
          {!isRaisedBeefHomeHost() && (
            <Route path={isClaraAppHost() ? "/" : "/clara"} element={<ClaraMobileShell />}>
            <Route index element={<ClaraMobileHome />} />
            <Route path="sales" element={<ClaraMobileSales />} />
            <Route path="alerts" element={<PageErrorBoundary pageName="Clara Alerts"><ClaraMobileAlerts /></PageErrorBoundary>} />
            <Route path="contact" element={<ClaraMobileContact />} />
            <Route path="account" element={<ClaraMobileAccount />} />
            <Route path="signin" element={<ClaraMobileSignIn />} />
            <Route path="t/:slug" element={<ClaraMobileTopic />} />
            <Route path="barn/:slug" element={<ClaraMobileBarn />} />
            <Route path="barn/:slug/:tool" element={<ClaraMobileBarnTool />} />
            </Route>
          )}

          <Route path="/reports/free/:slug" element={<FreeReportPage />} />
          {/* Public boxed beef page: owns /boxed-beef on the raisedbeef.com
              host (the platform hub keeps it elsewhere); /rb/boxed-beef is
              the always-available path. */}
          <Route path="/rb/boxed-beef" element={<RbBoxedBeefPage />} />
          {/* Public Packer Margin page, opened from the homepage rail card
              (owner, 2026-09-13). Public on every host; /market-pulse keeps
              the terminal version. */}
          <Route path="/packer-margin" element={gated(<RbPackerMarginPage />)} />
          <Route path="/rb/cattle-on-feed" element={<RbCattleOnFeedPage />} />
          <Route path="/rb/fed-cattle" element={<RbFedCattlePage />} />
          <Route path="/sale-reports" element={<RbSaleReportsPage />} />
          {/* The same tool in the site's current UI, at its own URL. The menu
              points here; /sale-reports stays routed because the footer, the
              old Header and every existing bookmark point at it. */}
          <Route path="/reports" element={gated(<RbReportsPage />)} />
          {/* The Weight Maps, one per 100 lb class (weightMaps.ts). Public and
              free, outside Layout and TierGate like the other rbnext tools.
              Normally answered by a prerendered document via vercel.json; these
              routes serve a client-side navigation. Written out rather than
              mapped so every path is greppable and link-checkable. */}
          <Route path="/4-wt-map" element={gated(<RbWeightMapPage band={400} />)} />
          <Route path="/5-wt-map" element={gated(<RbWeightMapPage band={500} />)} />
          <Route path="/6-wt-map" element={gated(<RbWeightMapPage band={600} />)} />
          <Route path="/7-wt-map" element={gated(<RbWeightMapPage band={700} />)} />
          <Route path="/8-wt-map" element={gated(<RbWeightMapPage band={800} />)} />
          <Route path="/9-wt-map" element={gated(<RbWeightMapPage band={900} />)} />
          <Route path="/10-wt-map" element={gated(<RbWeightMapPage band={1000} />)} />
          {/* Diesel. Like the Weight Maps: normally answered by a prerendered
              document via vercel.json, this route serves a client-side
              navigation. Account-gated on raisedbeef.com (siteAccess.ts). */}
          <Route path="/diesel" element={gated(<RbDieselPage />)} />
          {/* One index, its own page — and ONE component for all nineteen.
              §3: an index is a ROW, so adding a benchmark is an INSERT and
              this route serves it with no code and no deploy.
              PREMIUM (owner, 2026-10-09: "our indexes are our most valuable
              thing"; gate built 2026-10-12). The method stays reachable by a
              link we issue; the server-side lock on the figures is step 3. */}
          <Route path="/index" element={gated(<RbIndexPage />)} />
          {/* Unlisted, keyed (RB_INDEX_PROOF_PLAN.md §2B/§4). Declared before
              /index/:slug so "reconciliation" is never read as an index slug. */}
          <Route path="/index/reconciliation" element={<RbIndexReconciliationPage />} />
          {/* The published method (RB_INDEX_PROOF_PLAN.md §2C). Public. */}
          <Route path="/index/methodology" element={<RbIndexMethodologyPage />} />
          {/* The Daily Live Price's own statement (owner, 2026-10-08). */}
          <Route path="/index/methodology/daily" element={<RbDailyMethodologyPage />} />
          {/* The corrections log — every revision, before and after. Premium
              (owner, 2026-10-13): it prints index prices. */}
          <Route path="/index/corrections" element={gated(<RbIndexCorrectionsPage />)} />
          {/* The two all-barn boards, under Tools (owner, 2026-10-08). Premium. */}
          <Route path="/quotes-board" element={gated(<RbQuotesBoardPage />)} />
          <Route path="/weekly-averages" element={gated(<RbWeeklyAveragesPage />)} />
          {/* The calculation sheet — one index, one day or live (RB_INDEX_PROOF_PLAN.md §2A). Keyed. */}
          <Route path="/index/:slug/sheet/:date" element={<RbIndexSheetPage />} />
          {/* The Daily Live Price's day page — every change with its time;
              live while the day is on. Before /index/:slug, like the sheet. */}
          <Route path="/index/:slug/daily/:date" element={gated(<RbDailyPathPage />)} />
          <Route path="/index/:slug" element={gated(<RbIndexPage />)} />
          {/* Cattle Receipts. Head through the ring, week over week and month
              over month, on a same-barn panel. Account-gated on raisedbeef.com
              (siteAccess.ts) — it is derived history and rankings, which is the
              side of the free/gated line that asks for an account. */}
          <Route path="/receipts" element={gated(<RbReceiptsPage />)} />
          {/* The Daily Market Report's show-your-work page (design/daily-report/README.md §5.5a): public once an edition is approved or sent. */}
          <Route path="/report/:date" element={<RbDailyReportPage />} />
          {/* One barn, one sale, by weight class. A broadcast surface —
              Corbitt Wall reads it on camera during Feeder Flash.

              PREMIUM on the owner's call, 2026-10-12 — asked with the
              broadcast spelled out ("a wall would put a sign-up screen in
              front of his audience") and answered "Premium". Do not
              un-gate it on the old reasoning. */}
          <Route path="/sheet/:slug" element={gated(<RbSaleSheetPage />)} />
          {/* The sheet's front door: search, the sales that just finished, and
              the country by state. Before this, /sheet/:slug was the only way
              in, so reaching a second barn meant opening the wrong one first.
              Premium, like the sheet itself. */}
          <Route path="/sheet" element={gated(<RbSaleSheetLanding />)} />
          {/* Platform (PRO) marketing page - public on every host. */}
          <Route path="/platform" element={<PlatformPage />} />
          {isRaisedBeefHomeHost() && <Route path="/boxed-beef" element={<RbBoxedBeefPage />} />}
          {isRaisedBeefHomeHost() && <Route path="/cattle-on-feed" element={gated(<RbCattleOnFeedPage />)} />}
          {/* The Border Board — Mexican feeder cattle crossing into the Plains,
              USDA AMS 3486 (owner, 2026-10-27). Behind sign-in like Cattle on
              Feed: its discount ladder reads the barns as well as USDA. */}
          {isRaisedBeefHomeHost() && <Route path="/border" element={gated(<RbBorderPage />)} />}
          {/* The Flash screen (2026-10-27): Corbitt Wall's door — a barn picker and a 1920×1080 sale screen. */}
          {isRaisedBeefHomeHost() && <Route path="/flash" element={gated(<RbFlashPage />)} />}
          {isRaisedBeefHomeHost() && <Route path="/flash/lot/:lotId" element={gated(<RbFlashPage />)} />}
          {isRaisedBeefHomeHost() && <Route path="/flash/:slug" element={gated(<RbFlashPage />)} />}
          {/* Behind sign-in since it carries the barns as well as USDA (owner,
              2026-10-12: "this is behind the sign in"). */}
          {isRaisedBeefHomeHost() && <Route path="/fed-cattle" element={gated(<RbFedCattlePage />)} />}
          {/* Public National Steer Tracker. Same hook, same RPC and the same
              Realtime channel as the tier-gated terminal page at
              /tools/feeders/national-steer-tracker — only the UI differs, and
              there is no commentary panel. Deliberately outside Layout and
              TierGate: it is a free lead-magnet tool reachable with no account.
              rpc_national_steer_tracker is SECURITY INVOKER and reads fine
              anonymously (verified against the anon key). */}
          <Route path="/steer-tracker" element={gated(<SteerTrackerPublic />)} />

          {/* The barn ticker widened to a state: the 5 most recent lots per
              weight class across every reporting barn in it. Public and free,
              outside Layout and TierGate like the other rbnext tools.
              Distinct from /quick-view/state/XX, which is the tier-gated
              30-day VWAP page and has no lot rows in it at all.

              ONE route for all 33 states with a reporting barn. /ticker/missouri,
              /ticker/kansas, /ticker/south-dakota — same component, different
              param. Hierarchical rather than 33 flat routes: a crawler reads
              the structure, and it slots beside the /barn/:slug namespace that
              already exists. An unrecognised slug renders a named not-found and
              is noindexed rather than showing an empty board. */}
          {/* The two directory pages. The Cattle Ticker masthead has linked
              to /barns and /states since the homepage shipped and NEITHER
              route existed — both fell through to the catch-all NotFound, so
              two of the five nav items on the front door were dead links.
              They are also the crawl path: 258 barn boards and 33 state
              boards are in the sitemap and nothing on the site linked to them
              as a set, so a crawler reaching one had no route to the rest. */}
          {/* The lobby touch screen. Served at "/" on cattleticker.raisedbeef.com
              (below); /lobby is the same page on any host, for previews.
              PREMIUM on every host (owner, 2026-10-12) — the wall is inside
              LobbyPremium, so it is not a gated() route. */}
          <Route path="/lobby" element={<LobbyTickerPage />} />
          {/* The fat cattle tools (owner, 2026-09-16). Paid, like the other
              deeper tools: open during a new account's 30 days, then a
              subscription. The Terminal's own /fat-cattle-sales is untouched. */}
          <Route path="/fat-cattle" element={gated(<FatCattleHub />)} />
          <Route path="/fat-cattle/:slug" element={gated(<FatCattleBarnPage />)} />

          {/* The RaisedBeef phone app, in a browser.
              Mounted at /m/* so the UI can be reviewed on a real phone from a
              preview link before any native build exists; the Capacitor boot
              will open these same routes. Ungated on purpose — Prices is the
              free half of the app, and the paid tools carry their own gate
              when they land. */}
          <Route path="/m" element={<RbAppShell />}>
            {/* Home is the app's own front screen (owner, 2026-10-09); the
                website's board is one tap away at /m/prices as Live Ticker. */}
            <Route index element={<RbAppHome />} />
            <Route path="prices" element={<RbAppPrices />} />
            <Route path="indexes" element={<RbAppIndexes />} />
            <Route path="live" element={<CmcLiveHome />} />
            <Route path="live/b/:slug" element={<CmcLiveBarn />} />
            <Route path="index/methodology" element={<RbAppMethodology />} />
            <Route path="index/methodology/daily" element={<RbAppMethodology daily />} />
            <Route path="index/:slug" element={<RbAppIndex />} />
            <Route path="index/:slug/day/:date" element={<RbAppIndexDay />} />
            {/* The website's own pages, inside the frame — see SitePage. */}
            <Route path="wavg" element={<RbAppWeightedAverages />} />
            <Route path="qb" element={<RbAppQuotesBoard />} />
            <Route path="leaderboard" element={<RbAppLeaderboard />} />
            {/* Corbitt Wall's Feeder Flash, a Tools entry with its own 6 AM alert (owner, 2026-10-22). */}
            <Route path="feeder-flash" element={<RbAppFeederFlash />} />
            <Route path="toppers" element={<RbAppToppers />} />
            <Route path="clara-pro" element={<RbAppClaraPro />} />
            <Route path="maps" element={<RbAppWeightMaps />} />
            <Route path="boxed-beef" element={<RbAppBoxedBeef />} />
            <Route path="fed-cattle" element={<RbAppFedCattle />} />
            <Route path="packer-margin" element={<RbAppPackerMargin />} />
            <Route path="cattle-on-feed" element={<RbAppCattleOnFeed />} />
            {/* The Border Board, the website's page inside the frame (2026-10-27). */}
            <Route path="border" element={<RbAppBorder />} />
            <Route path="diesel" element={<RbAppDiesel />} />
            <Route path="receipts" element={<RbAppReceipts />} />
            {/* Clara Light, the app's own screens (owner, 2026-10-10). */}
            <Route path="clara" element={<RbAppClaraLight />} />
            {/* A barn's landing page in Clara Light, Beaver County trial (owner,
                2026-10-11). The first address, /m/clara/sale-barns, lands on Beaver's. */}
            <Route path="clara/barn/:slug" element={<RbAppBarnLanding />} />
            <Route path="clara/sale-barns" element={<Navigate to="/m/clara/barn/ok-beaver-county-stockyards" replace />} />
            <Route path="clara/:topic" element={<RbAppClaraLight />} />
            <Route path="steer-tracker" element={<RbAppSteerTracker />} />
            <Route path="fat-cattle" element={<RbAppFatHub />} />
            <Route path="fat-cattle/:slug" element={<RbAppFatBarn />} />
            <Route path="reports" element={<RbAppReports />} />
            <Route path="reports/:barn" element={<RbAppReport />} />
            <Route path="states/:alpha" element={<RbAppState />} />
            <Route path="t/*" element={<RbAppSitePage />} />
            <Route path="account" element={<RbAppAccount />} />
            {/* Screen B and "Save your account" — paying in the app (owner, 2026-10-22). */}
            <Route path="subscribe" element={<RbAppSubscribe />} />
            <Route path="save" element={<RbAppSave />} />
            <Route path="barns" element={<RbAppBarns />} />
            {/* One route for all 344 of them; the slug is the only thing that
                changes, and an unknown slug renders a named not-found rather
                than an empty board. */}
            <Route path="barns/:slug" element={<RbAppBarnBoard />} />
            <Route path="tools" element={<RbAppTools />} />
            <Route path="alerts" element={<RbAppAlerts />} />
            <Route path="alerts/inbox" element={<RbAppAlertInbox />} />
            <Route path="alerts/reach" element={<RbAppAlertReach />} />
            <Route path="you" element={<RbAppYou />} />
          </Route>
          {/* Foote's home screen. Mounted only on the Foote host so /foote is
              not a stray route on raisedbeef.com; the page itself still checks
              for a session and bounces to the door without one. */}
          {/* Walter (product 8). Host-only, like Foote: these paths do not
              exist on any other host. Each screen checks the seat itself
              (WalterShell → is_partner_member). */}
          {isWalterHost() && <Route path="/walter/us/:slug" element={<WalterUsBalanceSheet />} />}
          {isWalterHost() && <Route path="/walter/world/:slug" element={<WalterWorldTable />} />}
          {isWalterHost() && <Route path="/walter/this-month" element={<WalterThisMonth />} />}
          {isWalterHost() && <Route path="/walter/fas/:slug" element={<WalterFas />} />}
          {isWalterHost() && <Route path="/walter/us-livestock/:slug" element={<WalterLivestock />} />}
          {isWalterHost() && <Route path="/walter/ask" element={<WalterAsk />} />}
          {isWalterHost() && <Route path="/walter/corn-desk" element={<WalterCornDesk />} />}
          {/* Readers: the reading room and the article. Editors: the Newsroom (drafting, checks, publishing). */}
          {isWalterHost() && <Route path="/walter/corn-desk/stories" element={<WalterReadingRoom />} />}
          {isWalterHost() && <Route path="/walter/corn-desk/stories/:id" element={<WalterReaderStory />} />}
          {isWalterHost() && <Route path="/walter/corn-desk/newsroom" element={<WalterStories />} />}
          {isWalterHost() && <Route path="/walter/corn-desk/newsroom/:id" element={<WalterStoryEditor />} />}
          {/* A published story's share link: readable without signing in (walter_story_shared). */}
          {isWalterHost() && <Route path="/s/:token" element={<WalterSharedStory />} />}
          {isWalterHost() && <Route path="/walter/*" element={<Navigate to={WALTER_HOME} replace />} />}
          {isFooteHost() && <Route path="/foote" element={<FooteHome />} />}
          {/* Three separate add screens, on the owner's call: one combined
              picker buried the barn search under the states and the tools.
              /foote/add keeps working as a redirect rather than 404ing anyone
              who bookmarked it. */}
          {isFooteHost() && <Route path="/foote/barn/:slug" element={<FooteBarn />} />}
          {isFooteHost() && <Route path="/foote/state/:slug" element={<FooteState />} />}
          {isFooteHost() && <Route path="/foote/boxed-beef" element={<FooteBoxedBeef />} />}
          {isFooteHost() && <Route path="/foote/five-area" element={<FooteFiveArea />} />}
          {isFooteHost() && <Route path="/foote/fat-cattle" element={<FooteFatCattle />} />}
          {isFooteHost() && <Route path="/foote/fat-cattle/:slug" element={<FooteFatBarn />} />}
          {isFooteHost() && <Route path="/foote/packer-margin" element={<FootePackerMargin />} />}
          {isFooteHost() && (
            <Route path="/foote/weighted-averages" element={<FooteWeightedAverages />} />
          )}
          {isFooteHost() && <Route path="/foote/weight-map" element={<FooteWeightMap />} />}
          {isFooteHost() && <Route path="/foote/barn-prices" element={<FooteBarnPrices />} />}
          {isFooteHost() && <Route path="/foote/weight-slide" element={<FooteWeightSlide />} />}
          {isFooteHost() && <Route path="/foote/leaderboard" element={<FooteLeaderboard />} />}
          {isFooteHost() && (
            <Route path="/foote/carcass-weights" element={<FooteCarcassWeights />} />
          )}
          {isFooteHost() && (
            <Route path="/foote/cattle-on-feed" element={<FooteCattleOnFeed />} />
          )}
          {isFooteHost() && (
            <Route path="/foote/daily-slaughter" element={<FooteDailySlaughter />} />
          )}
          {isFooteHost() && <Route path="/foote/barns" element={<FooteAllBarns />} />}
          {isFooteHost() && <Route path="/foote/states" element={<FooteAllStates />} />}
          {isFooteHost() && <Route path="/foote/add" element={<Navigate to="/foote/add/barns" replace />} />}
          {isFooteHost() && <Route path="/foote/add/barns" element={<FooteAddBarns />} />}
          {isFooteHost() && <Route path="/foote/add/states" element={<FooteAddStates />} />}
          {isFooteHost() && <Route path="/foote/add/tools" element={<FooteAddTools />} />}

          <Route path="/barns" element={gated(<BarnsIndexPage />)} />
          <Route path="/states" element={gated(<StatesIndexPage />)} />

          <Route path="/ticker/:state" element={gated(<StateTickerPage />)} />

          {/* Both of these were public briefly today before the URL settled.
              A dead link on the front door costs more than two lines. */}
          <Route path="/missouri-ticker" element={<Navigate to="/ticker/missouri" replace />} />
          <Route path="/missouri-tracker" element={<Navigate to="/ticker/missouri" replace />} />

          {/* ── The public barn pages ────────────────────────────────────
              ONE component each, for every barn that has reported a sale in
              the last 90 days — 259 as of today, across 33 states. The slug is
              `<state>-<name>` (src/lib/barnSlug.ts), the same one the rest of
              this namespace and get_barn_ticker already use, so a barn has one
              address and not two.

              Order matters below: these two sit BEFORE /barn/:slug, because
              React Router v6 ranks static segments above dynamic ones and
              "ticker"/"live" must not be read as a barn name.

              Both are BASIC on raisedbeef.com since 2026-10-12 (they were
              free), outside Layout and TierGate, and both render a
              named, noindexed not-found for an unrecognised slug rather than
              an empty page under a blank heading. */}
          <Route path="/barn/:slug/ticker" element={gated(<BarnTickerPublic />)} />
          {/* A barn's own top pens (owner, 2026-09-13). Normally answered by a
              prerendered document via vercel.json; this serves a client-side
              navigation. Sits before /barn/:slug for the same reason as above. */}
          {/* Free, not gated (owner, 2026-10-16: the leaderboards are shared; no sign-in wall). */}
          <Route path="/barn/:slug/leaderboard" element={<BarnLeaderboardPublic />} />
          <Route path="/barn/:slug/live" element={gated(<BarnLivePublic />)} />

          {/* The two addresses Joplin was published at before the URL
              generalised. A dead link on a page a barn has already shared
              costs more than two lines. */}
          <Route
            path="/joplin-ticker"
            element={<Navigate to="/barn/mo-joplin-regional-stockyards/ticker" replace />}
          />
          <Route
            path="/joplin-live"
            element={<Navigate to="/barn/mo-joplin-regional-stockyards/live" replace />}
          />

          {/* New editorial sale-report page — public, standalone (no Layout
              chrome) so it can be shared as a publication. Lives at
              /preview while the visual is finalized; will replace
              /barn/:slug once approved. */}
          <Route path="/barn/:slug/preview" element={<BarnSaleReportPreview />} />
          {/* ⚠️ GATED DELIBERATELY, AND IT IS A SECURITY CHANGE, NOT A PRODUCT
              ONE. This page is the ONLY anonymous caller of
              `rpc_dv_daily_summary`, which takes a caller-set `p_limit` with no
              ceiling and a caller-set date window with no floor — i.e. it will
              hand a stranger the whole of `dv_daily_cohort_stats`, day by day,
              without ever touching the table. Closing the table does nothing
              while that door is open.

              So the function's EXECUTE is revoked from `anon`
              (20261003000002), and this route has to require an account or a
              signed-out visitor gets an empty archive. Gating the page is what
              makes the revoke safe; the revoke is what actually protects the
              data. Neither works alone.

              Costs nothing in search: the sitemap points Google at
              /barn/<slug>/ticker and /barn/<slug>/live, never /archive. */}
          <Route path="/barn/:slug/archive" element={gated(<BarnSaleArchive />)} />
          <Route path="/barn/:slug" element={gated(<BarnDetailPage />)} />
          {/* Iframe-friendly embed widget — standalone (no Layout). Lives
              at /embed/barn/:slug; the EmbedDialog on the report page
              generates the iframe snippet pointing here. */}
          <Route path="/embed/barn/:slug" element={<EmbedBarn />} />
          {/* Private sale-barn dashboard (desktop) — standalone authenticated
              route (NOT producer-tier-gated; a barn login is not a producer
              subscriber). Auth + barn-membership resolved inside the page.
              Skipped on the Clara app host, where "/my-barn" is the Clara
              owner launchpad instead — otherwise the two collide at the same
              path and route resolution is ambiguous. */}
          {/* The BARN's front door. Separate from /auth on purpose: the Sale
              Barn App + My Barn are one product sold to the barn, and a barn
              account never reaches Market Intelligence (owner, 2026-08-16). */}
          {!isClaraAppHost() && <Route path="/my-barn/login" element={<MyBarnLogin />} />}
          {!isClaraAppHost() && <Route path="/my-barn" element={<MyBarn />} />}
          <Route path="/cohort/:region/:weight" element={<CohortDetailPage />} />
          <Route path="/market/:category/:state/:weightClass" element={<MarketDetailPage />} />
          <Route path="/reports/premium/:slug" element={<PremiumReportPage />} />
          {/* raisedbeef.com has its OWN Privacy, Terms and Support (owner,
              2026-10-10 — separate from every other product); the platform
              hosts keep the platform's. legal/RAISEDBEEF_*.md. */}
          <Route path="/terms" element={isRaisedBeefHomeHost() ? <RbLegalPage doc="terms" /> : <Terms />} />
          <Route path="/features" element={<Features />} />
          <Route path="/privacy" element={isRaisedBeefHomeHost() ? <RbLegalPage doc="privacy" /> : <PrivacyPolicy />} />
          {/* Barn-app policy URLs for the store listings. Public, ungated —
              a Play reviewer opens these without an account. */}
          <Route path="/barn-privacy" element={<BarnPrivacy />} />
          <Route path="/barn-terms" element={<BarnTerms />} />
          {/* Per-barn: each names its app, which Play requires of the privacy
              and deletion URLs. src/barnApps/barnLegalPages.ts. */}
          <Route path="/barn-privacy/:slug" element={<BarnPrivacy />} />
          <Route path="/barn-terms/:slug" element={<BarnTerms />} />
          {/* The barn apps' OWN deletion door. /account-deletion renders the
              RaisedBeef consumer app's support page on this host — see
              src/pages/BarnDeletion.tsx. */}
          <Route path="/barn-deletion" element={<BarnDeletion />} />
          <Route path="/barn-deletion/:slug" element={<BarnDeletion />} />
          {/* The barn apps' Support URL. /support is the RaisedBeef app's page
              on this host — src/pages/BarnSupport.tsx. */}
          <Route path="/barn-support" element={<BarnSupport />} />
          <Route path="/barn-support/:slug" element={<BarnSupport />} />
          <Route path="/community-guidelines" element={<CommunityGuidelines />} />
          <Route path="/do-not-sell" element={<DoNotSell />} />
          <Route path="/support" element={isRaisedBeefHomeHost() ? <RbLegalPage doc="support" /> : <Support />} />
          <Route path="/account-deletion" element={isRaisedBeefHomeHost() ? <RbLegalPage doc="support" /> : <AccountDeletion />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          {/* Email-unsubscribe landing — handle-email-unsubscribe edge
              function 302s here with ?status= after performing the DB
              writes. Public, no auth (recipients click from email). */}
          <Route path="/unsubscribed" element={<Unsubscribed />} />

          {/* Root `/`. On raisedbeef.com → the homepage (usually served as
              prerendered HTML before this router runs — see RbGateHome /
              RbNextHome). On markets/apex → the ClaraLiveLanding public
              homepage. Skipped on the Clara app host, where the mobile app
              owns "/".

              This branch is the SPA fallback, not the usual path: middleware
              rewrites "/" to a prerendered document before the router ever
              loads. It still has to agree with middleware, though, or a
              client-side navigation back to "/" would render the OTHER
              homepage — see src/lib/rbHomeVersion.ts for why that takes two
              env vars set together. */}
          {!isClaraAppHost() && (
            <Route
              path="/"
              element={
                /* Foote is checked FIRST: foote.raisedbeef.com is a named
                   customer's own front door and must never fall through to a
                   RaisedBeef-branded homepage. */
                isFooteHost() ? (
                  <FooteSignIn />
                ) : isWalterHost() ? (
                  <WalterSignIn />
                ) : isLobbyHost() ? (
                  <LobbyTickerPage />
                ) : isRaisedBeefHomeHost() ? (
                  isRbHomeNext() ? (
                    <RbNextHome />
                  ) : (
                    <RbGateHome />
                  )
                ) : (
                  <ClaraLiveLanding />
                )
              }
            />
          )}

          {/* raisedbeef.com → DVAuction Live Sales, the live market page that
              used to be `/`. Public, free, self-contained under .rbhome. */}
          <Route path="/dvauction-live" element={<DvAuctionLivePage />} />

          {/* raisedbeef.com → Data & Workflow Solutions (the services arm). */}
          <Route path="/solutions" element={<RbSolutionsPage />} />

          {/* raisedbeef.com → National Leaderboard, its own standalone
              public page (no Layout chrome — self-contained under .rbhome).
              Public market data; free. */}
          {/* Rebuilt in the .rbnext language. The previous page is still on disk
              at src/pages/raisedbeef/LeaderboardPage.tsx and still imported as
              `LeaderboardPage`; putting it back is a one-word change here. */}
          {/* The leaderboards are FREE (owner, 2026-10-16: "so we can share without them hitting a sign in wall"). */}
          <Route path="/leaderboard" element={<LeaderboardNext cattle="Steers" />} />
          {/* Heifers at their own address (owner, 2026-10-15: "heifers will be a seperate address for now"). */}
          <Route path="/leaderboard/heifers" element={<LeaderboardNext cattle="Heifers" />} />
          {/* The National Calf Leaderboard (the Calf Tracker until 2026-10-16): the 300s through 600s, steer calves, its own
              photograph. Same page component; which board is a record in leaderboard-v3/classes.ts.
              ⚠️ AT /leaderboard/calves SINCE 2026-10-16 (owner: "it needs to live at leaderboard/calves...calf tracker
              will come later"). Its first address, /calf-tracker/national, redirects here so the day's shared links
              keep working — and is to be given up when the national calf tracker he means arrives at it.
              /calf-tracker (no suffix) is the older paid tool and stays. */}
          <Route path="/leaderboard/calves" element={<LeaderboardNext board="calves" />} />
          <Route path="/calf-tracker/national" element={<Navigate to="/leaderboard/calves" replace />} />
          {/* Toppers is BASIC (owner, 2026-10-09, built 2026-10-12). It was
              free so a barn's post of its win landed on the page; the owner
              put it in Basic with the other boards. A crawler and a link
              preview still read it (RequireAccount lets them through). */}
          <Route path="/toppers" element={gated(<RbToppersPage />)} />
          <Route path="/wall-display" element={<RbWallDisplayPage />} />
          {/* Clara — the front door to both products: the ask box hands off to
              Clara Pro, the tiles open Clara Light's deterministic reads.

              ⚠️ IT LIVES AT /clara AS OF 2026-10-03, which it could not before:
              that path was the Clara Live mobile shell until the owner retired
              the old app. The page is no longer "light" anything, so the old
              path would have been a lie about what it is.

              Gated on the owner's call, 2026-10-02 — see the note beside it in
              siteAccess.ts, which records why the source rule there points the
              other way. */}
          <Route path="/clara" element={gated(<RbClaraLightPage />)} />
          {/* ⚠️ THE OLD PATH KEEPS WORKING, AND KEEPS ITS QUERY. Links to
              /clara-light?topic=choice have been shared and are in the wild;
              a bare <Navigate to="/clara"> would drop the ?topic= and land
              every one of them on the front door instead of the market they
              named. */}
          <Route path="/clara-light" element={<ClaraLightRedirect />} />


          {/* raisedbeef.com → Sales: live-now board + week-ahead calendar.
              Public, free, self-contained under .rbhome. */}
          <Route path="/sales" element={<SalesPage />} />

          {/* raisedbeef.com's OWN account door. Public, and deliberately not
              /auth — that is the Terminal's door and stays that way. Signing up
              here creates an rb_accounts row and no profiles row, so it grants
              no Terminal access and no subscription tier. Same two-door shape
              as the barn product's /my-barn/login, for the same reason: one
              door serving two products has to guess which one the person
              wanted, and it guesses wrong. */}
          <Route path="/account" element={<RbAccountPage />} />
          <Route path="/membership" element={<RbMembershipPage />} />
          <Route path="/join" element={<RbJoinPage />} />

          {/* raisedbeef.com's own admin surface. Outside Layout and TierGate on
              purpose: administering the FREE site must not require a Terminal
              subscription. The page is not unguarded — it checks useIsAdmin()
              and every RPC behind it re-checks has_role() server-side, which is
              the real boundary. An admin role lives on the identity, not on
              either product, so it resolves the same whichever door you signed
              in through. */}
          <Route path="/manage/tools" element={<RbManageToolsPage />} />
          <Route path="/manage/leaderboard" element={<RbManageLeaderboardPage />} />
          {/* Where an RB index gets taken apart — index, state, barn, every
              lot, with the arithmetic printed at each level. Owner, relaying
              Rob: "show someone like the cme where our indexes and live
              numbers are coming from". Same guard shape as /manage/tools: the
              page checks useIsAdmin() and every RPC behind it re-checks
              has_role() server-side.

              ⚠️ NOT WRAPPED IN gated(). That helper is the free-site 30-day
              trial wall, not an admin check, and siteAccessRoutes.test.ts pins
              its list exactly — adding one here without touching siteAccess.ts
              fails that test, and it would be the wrong gate regardless. */}
          <Route path="/manage/index-audit" element={<RbIndexAuditPage />} />
          {/* Who is on what, and the free plans (billing step 5). Same guard
              shape: useIsAdmin() draws, every admin_rb_* function re-checks
              has_role(). Not in gated() for the same reason as above. */}
          <Route path="/manage/subscribers" element={<RbSubscribersPage />} />
          <Route path="/manage/daily-report" element={<RbManageDailyReportPage />} />
          {/* CMC Live's desk on the site where the board lives (owner,
              2026-10-26: the admin belongs on RaisedBeef.com, not the
              Terminal). Same guard shape; not in gated(). */}
          <Route path="/manage/cmc-live" element={<RbManageCmcLivePage />} />
          <Route path="/manage" element={<Navigate to="/manage/tools" replace />} />

          {/* Pricing is PUBLIC — deliberately outside Layout and TierGate.
              It sat inside both, and Layout redirects anyone without a
              session to /auth, so an anonymous visitor could not see the
              prices at all. The rbnext footer links here and PlatformPage
              links here four times; every one of those bounced to a login
              wall. A site whose whole job is to sell the Terminal has to be
              able to show what the Terminal costs.

              Signed-in users bounced here by TierGate now get the page
              without the sidebar chrome. That is the page's own stated
              intent — its header calls it "the public-facing plan selection
              page" — and a focused conversion page is the right shape for
              both audiences. It carries its own back button. */}
          <Route path="/pricing" element={<Pricing />} />

          {/* Protected routes with Layout chrome — Layout enforces
              auth redirect, TierGate (nested) enforces tier → /upgrade.
              Both redirects preserve attempted path for post-login /
              post-upgrade redirect. */}
          <Route element={<Layout />}>
          <Route element={<TierGate />}>
            <Route path="/upgrade" element={<Upgrade />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/cancel" element={<CheckoutCancel />} />
            <Route path="/onboarding/operator-type" element={<OnboardingOperatorType />} />
            <Route path="/dashboard" element={<Dashboard />} />
            {/* `/live` is now a regular sidebar tool — renders inside
                Layout (sidebar + TopNav chrome) alongside every other
                tool. Previously had its own standalone NavBar+TickerBar
                chrome outside Layout; that chrome was removed when the
                Clara Live Landing at `/` took over as the app's front
                door. */}
            <Route path="/live" element={<ClaraLiveHome />} />
            {/* Clara Live Cattle Consultant — full-page chat at
                /consultant. Choice-tier. Separate from the floating
                drawer surface (ConsultantBubble), which coexists. */}
            <Route path="/consultant" element={<ConsultantPage />} />

            {/* Section hub landing pages — Phase 3. Each hub shows
                decision-grouped tool tiles. Individual tool routes
                below still work — hubs are landings, not replacements. */}
            <Route path="/feeders" element={<FeedersHub />} />
            {!isRaisedBeefHomeHost() && <Route path="/fed-cattle" element={<FedCattleHub />} />}
            <Route path="/my-operation" element={<MyOperationHub />} />
            <Route path="/insights" element={<InsightsHub />} />
            {/* Phase 2 hubs (Session 53 sidebar reorg) */}
            {!isRaisedBeefHomeHost() && <Route path="/boxed-beef" element={<BoxedBeefHub />} />}
            <Route path="/cull-bulls" element={<CullBullsHub />} />
            <Route path="/cow-calf" element={<CowCalfHub />} />
            <Route path="/cow-calf/economics" element={<CowCalfEconomics />} />
            <Route path="/slaughter" element={<SlaughterHub />} />
            <Route path="/cattle-cycle" element={<CattleCycleHub />} />

            <Route path="/regional-markets" element={<RegionalMarkets />} />
            <Route path="/dv-auction" element={<DVAuctionReports />} />
            <Route path="/market-report" element={<MarketReport />} />
            <Route path="/calf-tracker" element={<CalfTracker />} />
            <Route path="/supply-demand" element={<SupplyDemand />} />
            {!isRaisedBeefHomeHost() && <Route path="/cattle-on-feed" element={<CattleOnFeed />} />}
            <Route path="/fed-cattle-auction" element={<FedCattleAuctionBarometer />} />
            <Route path="/fat-cattle-sales" element={<FatCattleSaleResults />} />
            <Route path="/barn-vs-packer" element={<BarnVsPackerPaid />} />
            <Route path="/tools/cattle-inventory" element={<CattleInventory />} />
            <Route path="/market-pulse" element={
              <PageErrorBoundary pageName="Market Pulse">
                <MarketPulse />
              </PageErrorBoundary>
            } />
            <Route path="/pasture-stress" element={<PastureStress />} />
            <Route path="/tools/per-head-supply" element={<PerHeadSupply />} />
            <Route path="/tools/cold-storage" element={<ColdStorage />} />
            <Route path="/tools/cot-positioning" element={<CotPositioning />} />
            <Route path="/market-calendar" element={<MarketReportCalendar />} />
            <Route path="/tools/feeding-extension" element={<FeedingExtension />} />
            <Route path="/tools/value-added-premium" element={<ValueAddedPremium />} />
            <Route path="/tools/barn-price-ranking" element={<BarnPriceLadder />} />
            <Route path="/tools/value-of-gain" element={<ValueOfGain />} />
            <Route path="/tools/video-forward-market" element={<VideoForwardMarket />} />
            <Route path="/tools/packer-posture" element={<PackerPosture />} />
            <Route path="/tools/primal-seasonality" element={<PrimalSeasonality />} />
            <Route path="/tools/choice-daily-cutout" element={<ChoiceDailyCutout />} />
            <Route path="/tools/usda-fed-cattle-forecast" element={<WasdeFedCattleForecast />} />
            <Route path="/tools/usda-forecast-path" element={<WasdeForecastPath />} />
            <Route path="/tools/usda-track-record" element={<WasdeTrackRecord />} />
            <Route path="/tools/usda-corn-outlook" element={<WasdeCornOutlook />} />
            <Route path="/tools/wasde-this-month" element={<WasdeThisMonth />} />
            <Route path="/tools/usda-beef-supply" element={<WasdeBeefSupply />} />
            <Route path="/tools/holiday-cut-tracker" element={<HolidayCutTracker />} />
            <Route path="/tools/steak-burger-gauge" element={<SteakBurgerGauge />} />
            <Route path="/tools/packer-leverage" element={<PackerLeverage />} />
            {/* Barn Fat Cattle vs Packer Price — RETIRED 2026-09-19 by owner
                decision. The tool measured barn fat cattle against the packer
                cash price, but barn fats are ~1% of the fed kill and the gap
                tracked the cash market at r² 0.209 — too weak to act on. Both
                its paths redirect to the hub so bookmarks and the retired
                `early-read` name still land somewhere real instead of blank. */}
            <Route
              path="/tools/fed-cattle/barn-fats-basis"
              element={<Navigate to="/fed-cattle" replace />}
            />
            <Route
              path="/tools/fed-cattle/early-read"
              element={<Navigate to="/fed-cattle" replace />}
            />
            <Route path="/tools/fed-cattle/recent-trades" element={<RecentTradeRecap />} />
            <Route path="/tools/fed-cattle/day-of-week" element={<DayOfWeekPattern />} />
            <Route path="/tools/fed-cattle/seasonal-setup" element={<SeasonalCashSetup />} />
            <Route path="/tools/fed-cattle/regional-pulse" element={<RegionalPulse />} />
            <Route path="/tools/fed-cattle/leverage-index" element={<FeedlotLeverageIndex />} />
            <Route path="/tools/fed-cattle/captive-supply" element={
              <PageErrorBoundary pageName="Captive Supply Tracker">
                <CaptiveSupplyTracker />
              </PageErrorBoundary>
            } />
            <Route path="/tools/fed-cattle/cash-vs-grid-spread" element={<CashVsGridSpread />} />
            <Route path="/tools/fed-cattle/price-distribution" element={
              <PageErrorBoundary pageName="Same-Week Price Distribution">
                <PriceDistribution />
              </PageErrorBoundary>
            } />
            <Route path="/tools/fed-cattle/cash-seasonality" element={
              <PageErrorBoundary pageName="Cash Trade Seasonality Calendar">
                <CashSeasonalityCalendar />
              </PageErrorBoundary>
            } />
            <Route path="/tools/feeders/national-steer-tracker" element={<NationalSteerTracker />} />
            <Route path="/tools/feeders/state-direct" element={
              <PageErrorBoundary pageName="State Feeder Direct">
                <StateFeederDirect />
              </PageErrorBoundary>
            } />
            <Route path="/tools/feeders/steer-tracker-daily" element={<SteerTrackerDaily />} />
            <Route path="/tools/feeders/clara-live-tracker" element={<ClaraLiveTracker />} />
            <Route path="/tools/fed-cattle/choice-select" element={<ChoiceSelectTracker />} />
            <Route path="/tools/drought" element={<DroughtIntelligence />} />
            <Route path="/tools/test-an-assumption" element={<AssumptionCheck />} />
            <Route path="/tools/drought-pressure" element={<DroughtPressure />} />
            <Route path="/freight" element={<FreightIntelligence />} />
            <Route path="/profitability" element={<ProfitabilityMargins />} />
            <Route path="/daily-brief" element={<Navigate to="/news/briefs" replace />} />
            <Route path="/data-integrations" element={<DataIntegrations />} />
            <Route path="/data-integrations/schema" element={<MarketDataSchema />} />
            <Route path="/data-integrations/architecture" element={<DataArchitecture />} />
            
            <Route path="/news/briefs" element={<NewsDailyBriefs />} />
            <Route path="/briefs/stocker" element={<StockerBriefs />} />
            <Route path="/briefs/saleday" element={<SaleDayBriefs />} />
            <Route path="/briefs/feedyard" element={<FeedyardBriefs />} />
            <Route path="/briefs" element={<BriefsHub />} />
            <Route path="/news/weekly" element={<WeeklyDigests />} />
            {/* /market-shift-tools route removed — page killed, tools were redundant with PMI */}
            {/* /quick-access route removed — replaced by global search in TopNav */}
            <Route path="/quick-view/barn/:name" element={<QuickViewBarn />} />
            <Route path="/quick-view/state/:state" element={<QuickViewState />} />
            <Route path="/quick-view/region/:region" element={<QuickViewRegion />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/report-builder" element={<ReportBuilder />} />
            <Route path="/email-dashboard" element={<EmailDashboard />} />
            <Route path="/admin/spotlights" element={<AdminSpotlights />} />
            {/* CMC Live check-first queue: the only way a held story moves. */}
            <Route path="/admin/cmc-live" element={<AdminCmcLive />} />
            <Route path="/admin/data-health" element={<AdminDataHealth />} />
            <Route path="/admin/clara-sponsors" element={<AdminClaraSponsors />} />
            <Route path="/admin/barn-sponsors" element={<AdminBarnSponsors />} />
            <Route path="/admin/board-ads" element={<AdminBoardAds />} />
            <Route path="/admin/barn-subscriptions" element={<AdminBarnSubscriptions />} />
            <Route path="/admin/partner-usage" element={<AdminPartnerUsage />} />
            <Route path="/admin/commentary-facts" element={<AdminCommentaryFacts />} />
            <Route path="/admin/sales" element={<AdminSales />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/barns" element={<AdminBarns />} />
            <Route path="/admin/barn-apps" element={<AdminBarnApps />} />
            <Route path="/admin/consultant-review" element={<AdminConsultantReview />} />
            <Route path="/admin/network" element={<AdminNetworkMembers />} />
            <Route path="/network" element={<NetworkDesk />} />
            <Route path="/admin/network-compliance" element={<AdminNetworkCompliance />} />
            <Route path="/admin/reports/cattle-on-feed/preview" element={<AdminCofReportPreview />} />
            <Route path="/admin/reports/cattle-on-feed" element={<AdminCofReports />} />
            <Route path="/admin/reports/cattle-on-feed/:releaseDate/draft" element={<AdminCofReportDraft />} />
            <Route path="/reports/cattle-on-feed/:releaseDate" element={<CofReportPublic />} />
          </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </BrowserRouter>
      </>
      )}
    </TooltipProvider>
    </NavModeProvider>
    </DashboardWidgetsProvider>
    </ThemeProvider>
    </UserProfileProvider>
    </ErrorBoundary>
  </QueryClientProvider>
);

export default App;

